import React, { useState, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { bankStatementService } from 'api/services';
import type {
  BankStatementLineDto, StatementSuggestionsDto, MatchSuggestionDto, ConfirmMatchItem, SuggestedCandidateDto, GroupSuggestionDto,
} from 'api/types';
import { money, fmtDate, lineAmount, lineDirection } from './ReconciliationWorksheet';

// ============================================================================
// SUGGESTED MATCHES (ADR-0055 §4, §6) — every unmatched line of a statement,
// grouped by confidence:
//   Confident — one convincing book entry (exact, or net of the mode's expected
//               charges within tolerance); pre-selected, confirm in one go.
//   Review    — several fit, a deduction is outside tolerance, or a batch
//               settlement was assembled; pick / adjust, then confirm.
//   No match  — post it from the line, match it by hand later, or ignore it.
// Charges confirmed together are booked as ONE Bank Charges journal. Nothing is
// matched until the user confirms.
// ============================================================================

const muted: React.CSSProperties = { color: 'var(--text-muted)' };
const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.5rem' };
const td: React.CSSProperties = { padding: '0.3rem 0.5rem', verticalAlign: 'top' };
const smallInput: React.CSSProperties = { width: 90, fontSize: '0.75rem', padding: '0.1rem 0.3rem', textAlign: 'right' };
const round2 = (n: number) => Math.round(n * 100) / 100;

type Tab = 'HIGH' | 'REVIEW' | 'NONE';
/** What the user picked for a line: one candidate entry, or the batch group (with its ticked receipts). */
type Pick = { kind: 'ENTRY'; entryId: string; gst?: number } | { kind: 'GROUP'; entryIds: string[]; gst?: number };

export interface ReconciliationSuggestionsModalProps {
  isOpen: boolean;
  statementId: string | null;
  canUpdate: boolean;
  onConfirmed: () => void;
  onClose: () => void;
}

/** GST share of a deduction as the suggestion split it (charge + GST = deduction). */
const gstShareOf = (charge: number, gst: number) => (charge + gst > 0 ? gst / (charge + gst) : 0);

export const ReconciliationSuggestionsModal: React.FC<ReconciliationSuggestionsModalProps> = ({ isOpen, statementId, canUpdate, onConfirmed, onClose }) => {
  const [data, setData] = useState<StatementSuggestionsDto | null>(null);
  const [lines, setLines] = useState<Map<string, BankStatementLineDto>>(new Map());
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>('HIGH');
  /** lineId → pick (presence = included in the confirm). */
  const [chosen, setChosen] = useState<Map<string, Pick>>(new Map());
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!statementId) return;
    setLoading(true);
    setFailures([]);
    try {
      const [{ suggestions }, statement] = await Promise.all([
        bankStatementService.getSuggestions(statementId),
        bankStatementService.getById(statementId),
      ]);
      setData(suggestions);
      setLines(new Map(statement.lines.map(l => [l.id, l])));
      // Confident lines start selected; review lines wait for the user.
      setChosen(new Map(suggestions.suggestions
        .filter(s => s.tier === 'HIGH' && s.suggestedEntryId)
        .map(s => [s.lineId, { kind: 'ENTRY', entryId: s.suggestedEntryId! } as Pick])));
      setTab(suggestions.high > 0 ? 'HIGH' : suggestions.review > 0 ? 'REVIEW' : 'NONE');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not suggest matches.');
    } finally {
      setLoading(false);
    }
  }, [statementId]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const byTier = useMemo(() => {
    const m: Record<Tab, MatchSuggestionDto[]> = { HIGH: [], REVIEW: [], NONE: [] };
    data?.suggestions.forEach(s => m[s.tier as Tab].push(s));
    return m;
  }, [data]);
  const suggestionOf = useMemo(() => new Map((data?.suggestions ?? []).map(s => [s.lineId, s])), [data]);

  const defaultPick = (s: MatchSuggestionDto): Pick | null =>
    s.suggestedEntryId ? { kind: 'ENTRY', entryId: s.suggestedEntryId }
    : s.group ? { kind: 'GROUP', entryIds: s.group.entries.map(e => e.entryId) } : null;

  const toggleLine = (s: MatchSuggestionDto) => setChosen(prev => {
    const next = new Map(prev);
    if (next.has(s.lineId)) next.delete(s.lineId);
    else { const p = defaultPick(s); if (p) next.set(s.lineId, p); }
    return next;
  });
  const setPick = (lineId: string, p: Pick) => setChosen(prev => new Map(prev).set(lineId, p));
  const setAll = (tier: Tab, on: boolean) => setChosen(prev => {
    const next = new Map(prev);
    byTier[tier].forEach(s => { const p = defaultPick(s); if (on && p) next.set(s.lineId, p); else next.delete(s.lineId); });
    return next;
  });

  // One entry may be picked for only one line in this confirm.
  const duplicateEntries = useMemo(() => {
    const seen = new Map<string, number>();
    chosen.forEach(p => (p.kind === 'ENTRY' ? [p.entryId] : p.entryIds).forEach(e => seen.set(e, (seen.get(e) ?? 0) + 1)));
    return new Set(Array.from(seen.entries()).filter(([, n]) => n > 1).map(([e]) => e));
  }, [chosen]);

  /** The deduction a pick books, with its split and ledgers. */
  const chargeOf = useCallback((lineId: string, p: Pick) => {
    const s = suggestionOf.get(lineId);
    const line = lines.get(lineId);
    if (!s || !line) return null;
    if (p.kind === 'ENTRY') {
      const c = s.candidates.find(x => x.entry.entryId === p.entryId);
      if (!c || c.actualCharge <= 0) return null;
      const gst = p.gst ?? c.suggestedGstAmount;
      return { deduction: c.actualCharge, charge: round2(c.actualCharge - gst), gst, chargesLedgerId: c.chargesLedgerId, gstLedgerId: c.gstLedgerId, ruleId: c.chargeRuleId, expected: c.expectedCharge, sourceLedgerId: null as string | null };
    }
    const g = s.group!;
    const gross = g.entries.filter(e => p.entryIds.includes(e.entryId)).reduce((a, e) => a + e.amount, 0);
    const deduction = round2(gross - lineAmount(line));
    const gst = p.gst ?? round2(deduction * gstShareOf(g.suggestedChargeAmount, g.suggestedGstAmount));
    return { deduction, charge: round2(deduction - gst), gst, chargesLedgerId: g.chargesLedgerId, gstLedgerId: g.gstLedgerId, ruleId: g.chargeRuleId, expected: g.expectedCharge, sourceLedgerId: g.fromClearingLedger ? g.sourceLedgerId : null };
  }, [suggestionOf, lines]);

  const handleConfirm = useCallback(async () => {
    if (!data || chosen.size === 0) return;
    const matches: ConfirmMatchItem[] = [];
    chosen.forEach((p, lineId) => {
      const s = suggestionOf.get(lineId);
      const ch = chargeOf(lineId, p);
      matches.push({
        lineIds: [lineId],
        entryIds: p.kind === 'ENTRY' ? [p.entryId] : p.entryIds,
        method: 'SUGGESTED', tier: s?.tier ?? null,
        ...(ch ? {
          chargeAmount: ch.charge, gstAmount: ch.gst,
          chargesLedgerId: ch.chargesLedgerId ?? null, gstLedgerId: ch.gst > 0 ? ch.gstLedgerId ?? null : null,
          chargeRuleId: ch.ruleId ?? null, expectedCharge: ch.expected ?? null, sourceLedgerId: ch.sourceLedgerId,
        } : {}),
      });
    });
    setSaving(true);
    try {
      const { result } = await bankStatementService.confirmMatches(matches);
      if (result.confirmed > 0) notificationService.toast.success(`${result.confirmed} line(s) matched.`);
      if (result.failures.length > 0) {
        setFailures(result.failures.map(f => f.message));
        notificationService.toast.warning(`${result.failures.length} could not be matched — see the list.`);
      }
      onConfirmed();
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not confirm the matches.');
    } finally {
      setSaving(false);
    }
  }, [data, chosen, suggestionOf, chargeOf, onConfirmed, load]);

  if (!isOpen) return null;

  const totalCharges = Array.from(chosen.entries()).reduce((a, [lineId, p]) => a + (chargeOf(lineId, p)?.deduction ?? 0), 0);

  const lineCell = (lineId: string) => {
    const l = lines.get(lineId);
    if (!l) return <span style={muted}>—</span>;
    const dep = lineDirection(l) === 'DEPOSIT';
    return (
      <div>
        <div><strong>{fmtDate(l.txnDate)}</strong> · <span style={{ color: dep ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{dep ? 'Deposit' : 'Withdrawal'} {money(lineAmount(l))}</span></div>
        <div style={{ ...muted, fontSize: '0.75rem', maxWidth: 330, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.description || ''}>
          {l.description || '—'}{l.reference ? ` · ${l.reference}` : ''}
        </div>
      </div>
    );
  };

  /** The deduction of a picked line, with the GST share editable (charge = deduction − GST). */
  const chargeEditor = (lineId: string, p: Pick) => {
    const ch = chargeOf(lineId, p);
    if (!ch) return null;
    const outside = ch.expected != null && Math.abs(ch.deduction - ch.expected) > 0.5 && p.kind === 'ENTRY';
    return (
      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', padding: '0.3rem 0.45rem', background: 'var(--bg-secondary)', borderRadius: '0.35rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>Charges <strong>{money(ch.deduction)}</strong>{ch.expected != null && <span style={{ color: outside ? '#d97706' : 'var(--text-muted)' }}> (expected {money(ch.expected)})</span>}</span>
        <span style={muted}>= charge {money(ch.charge)} + GST</span>
        <input type="number" step="0.01" value={ch.gst} disabled={!canUpdate || !ch.gstLedgerId}
          onChange={(e) => setPick(lineId, { ...p, gst: Math.min(ch.deduction, Math.max(0, parseFloat(e.target.value) || 0)) })} style={smallInput} />
        {!ch.chargesLedgerId && <span style={{ color: '#dc2626' }}>No charges ledger on the rule — set one under Charge Rules.</span>}
      </div>
    );
  };

  const candidateRow = (s: MatchSuggestionDto, c: SuggestedCandidateDto) => {
    const p = chosen.get(s.lineId);
    const picked = (p?.kind === 'ENTRY' ? p.entryId : s.suggestedEntryId) === c.entry.entryId && (!p || p.kind === 'ENTRY');
    const dup = !!p && picked && duplicateEntries.has(c.entry.entryId);
    return (
      <label key={c.entry.entryId} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.15rem 0', cursor: canUpdate ? 'pointer' : 'default' }}>
        {(s.candidates.length > 1 || !!s.group) && (
          <input type="radio" name={`pick-${s.lineId}`} checked={picked} disabled={!canUpdate}
            onChange={() => setPick(s.lineId, { kind: 'ENTRY', entryId: c.entry.entryId })} style={{ marginTop: 3 }} />
        )}
        <span style={{ flex: 1 }}>
          <strong>{c.entry.transactionNo}</strong> · {fmtDate(c.entry.transactionDate)} · <strong>{money(c.entry.amount)}</strong>
          {c.entry.instrumentType && <span style={muted}> · {c.entry.instrumentType}</span>}
          {c.entry.partyName && <span style={muted}> · {c.entry.partyName}</span>}
          {c.entry.instrumentNo && <span style={muted}> · No. {c.entry.instrumentNo}</span>}
          <div style={{ fontSize: '0.72rem', color: c.amountMatches || c.chargeWithinTolerance ? 'var(--text-secondary)' : '#d97706' }}>{c.reasons.join(' · ')}</div>
          {dup && <div style={{ fontSize: '0.72rem', color: '#dc2626' }}>Also picked for another line — only one can have it.</div>}
          {p && picked && chargeEditor(s.lineId, p)}
        </span>
      </label>
    );
  };

  const groupBlock = (s: MatchSuggestionDto, g: GroupSuggestionDto) => {
    const p = chosen.get(s.lineId);
    const asGroup = p?.kind === 'GROUP' ? p : null;
    const ticked = new Set(asGroup ? asGroup.entryIds : g.entries.map(e => e.entryId));
    const sel = g.entries.filter(e => ticked.has(e.entryId)).reduce((a, e) => a + e.amount, 0);
    const toggleEntry = (id: string) => {
      const ids = new Set(ticked);
      if (ids.has(id)) ids.delete(id); else ids.add(id);
      setPick(s.lineId, { kind: 'GROUP', entryIds: Array.from(ids), gst: undefined });
    };
    return (
      <div style={{ marginTop: '0.25rem', border: '1px dashed var(--border-color)', borderRadius: '0.4rem', padding: '0.35rem 0.5rem' }}>
        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.78rem', fontWeight: 600 }}>
          {s.candidates.length > 0 && (
            <input type="radio" name={`pick-${s.lineId}`} checked={!!asGroup} disabled={!canUpdate}
              onChange={() => setPick(s.lineId, { kind: 'GROUP', entryIds: g.entries.map(e => e.entryId) })} />
          )}
          Batch settlement{g.modeCode ? ` (${g.modeCode})` : ''}{g.fromClearingLedger ? ' from the clearing account' : ''} — receipts {money(sel)}, credited {money(lineAmount(lines.get(s.lineId)!))}
        </label>
        {g.entries.map(e => (
          <label key={e.entryId} style={{ display: 'flex', gap: '0.45rem', fontSize: '0.75rem', padding: '0.1rem 0 0.1rem 1.2rem' }}>
            <input type="checkbox" checked={ticked.has(e.entryId)} disabled={!canUpdate} onChange={() => toggleEntry(e.entryId)} />
            <span><strong>{e.transactionNo}</strong> · {fmtDate(e.transactionDate)} · {money(e.amount)}{e.partyName && <span style={muted}> · {e.partyName}</span>}</span>
          </label>
        ))}
        {asGroup && chargeEditor(s.lineId, asGroup)}
      </div>
    );
  };

  const tabButton = (t: Tab, label: string, count: number, color: string) => (
    <button key={t} onClick={() => setTab(t)} className={`btn ${tab === t ? 'btn-primary' : 'btn-secondary'}`}
      style={{ fontSize: '0.8125rem', padding: '0.3rem 0.8rem' }}>
      {label} <span className="badge" style={{ background: color, color: '#fff', marginLeft: 4 }}>{count}</span>
    </button>
  );

  const linkStyle: React.CSSProperties = { background: 'none', border: 'none', padding: 0, fontSize: '0.78rem', color: '#2563eb', cursor: 'pointer' };
  const selectedInTab = byTier[tab].filter(s => chosen.has(s.lineId)).length;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl" style={{ minHeight: 'auto' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Suggested Matches</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {loading && !data && <div style={{ ...muted, textAlign: 'center', padding: '2rem' }}>Looking for matches in the bank book…</div>}
          {data && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                {tabButton('HIGH', 'Confident', data.high, '#16a34a')}
                {tabButton('REVIEW', 'To review', data.review, '#d97706')}
                {tabButton('NONE', 'No match', data.none, '#6b7280')}
                <span style={{ ...muted, fontSize: '0.78rem', marginLeft: 'auto' }}>{data.unmatchedLines} unmatched line(s) · nothing is matched until you confirm</span>
              </div>

              {failures.length > 0 && (
                <div style={{ border: '1px solid #dc2626', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.78rem' }}>
                  {failures.map((f, i) => <div key={i}>{f}</div>)}
                </div>
              )}

              {tab !== 'NONE' && byTier[tab].length > 0 && (
                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.78rem', marginBottom: '0.4rem' }}>
                  <button style={linkStyle} onClick={() => setAll(tab, true)}>Select all</button>
                  <button style={linkStyle} onClick={() => setAll(tab, false)}>Clear</button>
                  <span style={muted}>{selectedInTab} selected here · {chosen.size} in total</span>
                </div>
              )}

              <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', maxHeight: '50vh', overflow: 'auto' }}>
                <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead><tr style={{ background: 'var(--grid-header-bg, #dbeafe)', position: 'sticky', top: 0, zIndex: 1 }}>
                    {tab !== 'NONE' && <th style={{ ...th, width: 32 }} />}
                    <th style={{ ...th, width: 340 }}>Statement line</th>
                    <th style={th}>{tab === 'NONE' ? 'What to do' : 'Book entry'}</th>
                  </tr></thead>
                  <tbody>
                    {byTier[tab].length === 0 && (
                      <tr><td colSpan={3} style={{ ...td, textAlign: 'center', ...muted, padding: '1.25rem' }}>Nothing here.</td></tr>
                    )}
                    {tab === 'NONE' && byTier.NONE.map(s => (
                      <tr key={s.lineId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={td}>{lineCell(s.lineId)}</td>
                        <td style={{ ...td, ...muted, fontSize: '0.78rem' }}>
                          No unreconciled book entry fits in the date window. Use <strong>Match…</strong> on the line to search the
                          whole bank book (a difference can be booked as charges there), <strong>post</strong> the entry from the line, or <strong>Ignore</strong> it.
                        </td>
                      </tr>
                    ))}
                    {tab !== 'NONE' && byTier[tab].map(s => {
                      const on = chosen.has(s.lineId);
                      return (
                        <tr key={s.lineId} style={{ borderBottom: '1px solid var(--border-color)', background: on ? 'rgba(37,99,235,0.06)' : undefined }}>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <input type="checkbox" checked={on} onChange={() => toggleLine(s)} disabled={!canUpdate} />
                          </td>
                          <td style={td}>{lineCell(s.lineId)}{s.note && <div style={{ fontSize: '0.72rem', color: '#d97706', marginTop: 2 }}>{s.note}</div>}</td>
                          <td style={td}>
                            {s.candidates.map(c => candidateRow(s, c))}
                            {s.group && groupBlock(s, s.group)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalCharges > 0 && (
                <div style={{ fontSize: '0.78rem', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Charges of <strong>{money(totalCharges)}</strong> on the selected lines will be booked as one Bank Charges journal.
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
          {canUpdate && (
            <button className="btn btn-primary" onClick={handleConfirm} disabled={saving || chosen.size === 0 || duplicateEntries.size > 0}
              title={duplicateEntries.size > 0 ? 'One book entry is picked for two lines' : undefined}>
              <IconSystem.check size={16} /> {saving ? 'Confirming…' : `Confirm selected (${chosen.size})`}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ReconciliationSuggestionsModal;
